import React, {
  createContext,
  useCallback,
  useContext,
  useRef,
  useState,
} from "react";
import axiosInstance from "../api/axiosInstance";

const ProjectMembersContext = createContext(null);

function normalizeMembers(data) {
  if (Array.isArray(data)) return data;
  if (data && Array.isArray(data.members)) return data.members;
  if (data && Array.isArray(data.items)) return data.items;
  return [];
}

export const ProjectMembersProvider = ({ children }) => {
  const [membersByProject, setMembersByProject] = useState({});
  const cacheRef = useRef({});
  const inFlightRef = useRef({});

  const fetchProjectMembers = useCallback((projectId) => {
    if (!projectId) return Promise.resolve([]);
    if (cacheRef.current[projectId]) {
      return Promise.resolve(cacheRef.current[projectId]);
    }
    if (inFlightRef.current[projectId]) {
      return inFlightRef.current[projectId];
    }
    const request = axiosInstance
      .get(`/projects/${projectId}/members`)
      .then((res) => {
        const members = normalizeMembers(res.data);
        cacheRef.current[projectId] = members;
        setMembersByProject((prev) => ({ ...prev, [projectId]: members }));
        return members;
      })
      .catch((err) => {
        console.error("[ProjectMembersContext] fetch members failed", err);
        return [];
      })
      .finally(() => {
        delete inFlightRef.current[projectId];
      });
    inFlightRef.current[projectId] = request;
    return request;
  }, []);

  return (
    <ProjectMembersContext.Provider
      value={{ membersByProject, fetchProjectMembers }}
    >
      {children}
    </ProjectMembersContext.Provider>
  );
};

export const useProjectMembers = () => {
  const ctx = useContext(ProjectMembersContext);
  if (!ctx) {
    return { membersByProject: {}, fetchProjectMembers: () => Promise.resolve([]) };
  }
  return ctx;
};

export default ProjectMembersProvider;
